<script setup lang="ts">
    import { nextTick, ref } from 'vue';
    import NestedCheckboxListItems from './NestedCheckboxListItems.vue';
    import { EchoFilterCheckbox } from '@/js/services/api/filter/types';
    import { __ } from '@/js/stores/expressions';

    const props = defineProps<{
        checkboxes: EchoFilterCheckbox[];
        expressionBase?: string;
    }>();

    function getParentCheckbox(
        checkboxes: EchoFilterCheckbox[],
        checkbox: EchoFilterCheckbox
    ): EchoFilterCheckbox | null {
        for (let item of checkboxes) {
            if (item.children.includes(checkbox)) {
                return item;
            }

            if (getParentCheckbox(item.children, checkbox)) {
                return getParentCheckbox(item.children, checkbox);
            }
        }

        return null;
    }

    function updateChildren(item: EchoFilterCheckbox) {
        if (item.children.length > 0) {
            item.children.forEach((child) => {
                child.isSelected = item.isSelected;
                updatedCheckbox(child);
                updateChildren(child);
            });
        }
    }

    function updatedCheckbox(checkbox: EchoFilterCheckbox) {
        nextTick(() => {
            let parent = getParentCheckbox(props.checkboxes, checkbox);

            while (parent) {
                parent.isSelected = parent.children.every((child) => child.isSelected);

                parent = getParentCheckbox(props.checkboxes, parent);

                nextTick(() => {
                    if (parent) {
                        updatedCheckbox(parent);
                    }
                });
            }
        });
    }

    const nestedCheckboxWasUpdated = (checkbox: EchoFilterCheckbox) => {
        updatedCheckbox(checkbox);
        updateChildren(checkbox);
    };

    const openStates = ref<Record<string, boolean>>({});
    const filter = ref('');
</script>

<template>
    <div class="mx-4 pb-4 mb-4 border-b border-b-gray-lightest">
        <div
            class="text-input rounded-sm border custom-focus-within flex flex-wrap items-center overflow-hidden p-1"
        >
            <input
                v-model="filter"
                :placeholder="__('search')"
                :aria-label="__('search')"
                type="text"
                class="border-0 focus:ring-0 flex-grow py-0.5 px-1.5 min-w-24 w-24 text-caption"
            />
        </div>
    </div>
    <div v-if="checkboxes.length === 0" class="px-4 pb-5">
        {{ __('filter-no-options') }}
    </div>
    <div v-else class="-ml-1.5 pr-4 pb-5">
        <NestedCheckboxListItems
            :checkboxes="checkboxes"
            :open-states="openStates"
            :filter="filter"
            :expression-base="props.expressionBase"
            @updated="nestedCheckboxWasUpdated($event)"
        />
    </div>
</template>
